import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getValidNotificationImageUrl } from "utils/notification";
import { logout } from "store/authSlice";

export const SESSION_EXPIRED_KEY = "sessionExpired";

const productionApiUrl =
  "http://k8s-default-wishaur-a2c24b92b7-1213354816.ap-south-1.elb.amazonaws.com";

// Helper function to determine the base URL dynamically
const dynamicBaseQuery = async (args, api, extraOptions) => {
  const adminApiUrl = process.env.REACT_APP_ADMIN_API_URL || productionApiUrl;
  const authApiUrl = process.env.REACT_APP_AUTH_API_URL || productionApiUrl;

  let baseUrl = adminApiUrl;
  let passType = false;

  if (args?.url?.startsWith("auth")) {
    baseUrl = authApiUrl;
    passType = true;
  }
 
  const token = localStorage.getItem('authToken');
  const rawBaseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      // Set Content-Type for auth endpoints
      if (passType) {
        headers.set('Content-Type', 'application/json');
      }
      // RTK Query automatically sets Content-Type: application/json when body is an object
      // So we don't need to set it manually for other endpoints
      headers.set('Accept', 'application/json');
      headers.set('Access-Control-Allow-Origin', '*');
      headers.set('Access-Control-Allow-Headers', 'Origin, X-Requested-With, Content-Type, Accept');
      headers.set('Access-Control-Allow-Methods', 'GET,HEAD,PUT,PATCH,POST,DELETE,OPTIONS');
      headers.set('Access-Control-Allow-Credentials', 'true');
    
      if (token) {
        headers.set('authentication', `${token}`);
      }
      return headers;
    },
  });
 // const rawBaseQuery = fetchBaseQuery({ baseUrl });
  return rawBaseQuery(args, api, extraOptions);
};

const baseQueryWithSessionHandling = async (args, api, extraOptions) => {
  const result = await dynamicBaseQuery(args, api, extraOptions);
  const status = result.error?.status;
  const isAuthRequest = args?.url?.startsWith("auth");

  if (!isAuthRequest && (status === 401 || status === 403)) {
    api.dispatch(logout());
    sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
    if (window.location.hash !== "#/") {
      window.location.hash = "#/";
    }
  }

  return result;
};

// Backend Api
export const api = createApi({
  baseQuery: baseQueryWithSessionHandling,
  reducerPath: "adminApi",
  
  // tags
  tagTypes: [
    "User",
    "Products",
    "Customers",
    "CustomerWallet",
    "Transactions",
    "Geography",
    "Sales",
    "Admins",
    "Performance",
    "Dashboard",
    "Category",
    "WithdrawRequests",
    "AppSettings",
    "WithdrawalsSettings",
    "NameChangeSettings",
    "AdsSettings",
    "ReferralMessage",
    "LeaderboardStreakSettings",
    "LiveDashboardTicker",
    "ProductWishes",
    "ProductImages",
    "ListedLeaderboards",
  ],
  // endpoints
  endpoints: (build) => ({
    adminLogin: build.mutation({
      query: ({ email, password }) => {
        const data = {
          email: email,
          password: password,
        };
        return {
          url: "auth/loginAdmin",
          method: "POST",
          body: JSON.stringify(data),
        };
      },
    }),
    getCategory: build.query({
      query: () => "admin/category/get",
      providesTags: ["Category"],
    }),
    deleteCategory: build.mutation({
      query: (categoryId) => ({
        url: `admin/category/${categoryId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Category"],
    }),
    getProductImages: build.query({
      query: (productId) => `admin/product/getImages/${productId}`,
      providesTags: (_result, _error, productId) => [
        { type: "ProductImages", id: productId },
      ],
    }),
    saveProductImage: build.mutation({
      query: ({ productId, image }) => {
        const formData = new FormData();
        formData.append("productId", productId);
        formData.append("file", image);
        return {
          url: "admin/product/saveImage",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { productId }) => [
        "Product",
        { type: "ProductImages", id: productId },
      ],
    }),
    createQuestions: build.mutation({
      query: ({ type, count }) => {
        return {
          url: "admin/question/generate",
          method: "POST",
          body: {
            "type" : type,
            "count" : count
        },
        };
      },
      invalidatesTags: ["Question"],
    }),
    createProductQuestions: build.mutation({
      query: ({ questionFormat, productId,questionCount }) => {
        return {
          url: "admin/product/createProductQuestion",
          method: "POST",
          body: {
            "questionFormat" : questionFormat,
            "productId" : productId,
            "questionCount":questionCount
        },
        };
      },
      invalidatesTags: ["Question"],
    }),
    getProductQuestions: build.mutation({
      query: (productId) => ({
        url: `admin/product/getProductQuestionsType/${productId}`,
        method: "GET",
      }),
      invalidatesTags: ["Category"],
    }),
    deleteProductQuestion: build.mutation({
      query: (id) => ({
        url: `admin/product/deleteProductQuestion/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Category"],
    }),
    deleteImage: build.mutation({
      query: ({ imageId }) => ({
        url: `admin/product/deleteImage/${imageId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { productId }) => [
        "Product",
        ...(productId ? [{ type: "ProductImages", id: productId }] : []),
      ],
    }),
    createCategory: build.mutation({
      query: ({ name, image }) => {
        const formData = new FormData();
        formData.append("name", name);
        formData.append("file", image);
        return {
          url: "admin/category/create",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Category"],
    }),
    updateCategory: build.mutation({
      query: ({ name, image }) => {
        const formData = new FormData();
        formData.append("name", name);
        formData.append("file", image);
        return {
          url: "admin/category/update",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Category"],
    }),
    getQuestions: build.query({
      query: () => "admin/question",
      providesTags: ["Product"],
    }),
    getCustomers: build.query({
      query: ({ page = 1, limit = 10, search, status }) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(limit));
        if (search?.trim()) params.set("search", search.trim());
        if (status?.trim() && status.trim().toUpperCase() !== "ALL") {
          params.set("status", status.trim());
        }
        return `admin/user/all?${params.toString()}`;
      },
      providesTags: ["Customers"],
    }),
    getCustomerWallet: build.query({
      query: (userId) => `admin/user/${userId}/wallet`,
      providesTags: (_result, _error, userId) => [
        { type: "CustomerWallet", id: userId },
      ],
    }),
    updateCustomerUpi: build.mutation({
      query: ({ userId, upiId }) => ({
        url: `admin/user/${userId}/upi`,
        method: "PATCH",
        body: { upiId: upiId ?? "" },
      }),
      invalidatesTags: ["Customers"],
    }),
    updateCustomerWallet: build.mutation({
      query: ({ userId, amount, operation, note }) => ({
        url: `admin/user/${userId}/wallet`,
        method: "PATCH",
        body: {
          amount: Number(amount),
          operation,
          ...(note != null && String(note).trim() !== "" ? { note: String(note).trim() } : {}),
        },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        "Customers",
        { type: "CustomerWallet", id: userId },
      ],
    }),
    getWithdrawRequests: build.query({
      query: ({ page = 1, limit = 20, paymentStatus }) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(limit));
        if (paymentStatus) {
          params.set("paymentStatus", paymentStatus);
        }
        return `admin/withdraw-requests?${params.toString()}`;
      },
      providesTags: ["WithdrawRequests"],
    }),
    updateWithdrawRequestStatus: build.mutation({
      query: ({ id, paymentStatus }) => ({
        url: `admin/withdraw-requests/${id}`,
        method: "PATCH",
        body: { paymentStatus },
      }),
      invalidatesTags: ["WithdrawRequests"],
    }),
    getProducts: build.query({
      query: () => "admin/product/get",
      providesTags: ["Product"],
    }),
    deleteProduct: build.mutation({
      query: (productId) => ({
        url: `admin/product/${productId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Product"],
    }),
    cloneProduct: build.mutation({
      query: (productId) => ({
        url: `admin/product/clone/${productId}`,
        method: "POST",
      }),
      invalidatesTags: ["Product"],
    }),
    getProductWishes: build.query({
      query: (productId) => ({
        url: `admin/product/getProductWishes/${productId}`,
      }),
      providesTags: (result, error, productId) => [
        { type: "ProductWishes", id: productId },
      ],
    }),
    getContestLiveStats: build.query({
      query: ({ productId, playToken }) => ({
        url: `admin/product/getContestLiveStats/${productId}/${encodeURIComponent(playToken)}`,
      }),
    }),
    getLiveDashboardStats: build.query({
      query: ({ productId, playToken } = {}) => ({
        url: "admin/product/getLiveDashboardStats",
        params: {
          ...(productId ? { productId } : {}),
          ...(playToken ? { playToken } : {}),
        },
      }),
    }),
    getPlayTableHistory: build.query({
      query: ({ productId, playToken, page = 1, pageSize = 25 }) => ({
        url: `admin/product/getPlayTableHistory/${productId}/${encodeURIComponent(playToken)}`,
        params: { page, pageSize },
      }),
    }),
    getComboWinners: build.query({
      query: ({ productId, playToken }) => ({
        url: `admin/product/getComboWinners/${productId}/${encodeURIComponent(playToken)}`,
      }),
    }),
    completeProductWish: build.mutation({
      query: ({ productWishId }) => ({
        url: `admin/product/completeProductWish/${productWishId}`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { productId }) => [
        { type: "ProductWishes", id: productId },
        "Product",
      ],
    }),
    announceProductWishResult: build.mutation({
      query: ({ productWishId }) => ({
        url: `admin/product/announceProductWishResult/${productWishId}`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { productId }) => [
        { type: "ProductWishes", id: productId },
        "Product",
      ],
    }),
    setProductActiveStatus: build.mutation({
      query: ({ productId, active }) => ({
        url: `admin/product/setProductActiveStatus/${productId}`,
        method: "PATCH",
        body: { active },
      }),
      invalidatesTags: ["Product"],
    }),
    setProductPlayableStatus: build.mutation({
      query: ({ productId, playable }) => ({
        url: `admin/product/setProductPlayableStatus/${productId}`,
        method: "PATCH",
        body: { playable },
      }),
      invalidatesTags: ["Product"],
    }),
    updateProductWishYoutube: build.mutation({
      query: ({ productWishId, youtubeVideoId }) => ({
        url: `admin/product/updateProductWishYoutube/${productWishId}`,
        method: "PATCH",
        body: { youtubeVideoId },
      }),
      invalidatesTags: (result, error, { productId }) => [
        { type: "ProductWishes", id: productId },
      ],
    }),
    createProduct: build.mutation({
      query: ({ productName,
        productDescription,
        terms,
        categoryId,
        productPrice,
        totalWish,
        totalQuestions,
        status,
        image,deductingWish,productType,isFirstWinnerGotProduct,isPlayable,isTimeBasedEvent ,startDateTime,endDateTime }) => {
        const formData = new FormData();
       

        let utcDateStart;
        let utcDateEnd;
          
          if(isTimeBasedEvent === "0"){
             const date = new Date();
             utcDateStart = date.toISOString();
            const dateend = new Date();
            utcDateEnd = dateend.toISOString();
          }else{
            const date = new Date(startDateTime);
            utcDateStart = date.toISOString();
            const dateend = new Date(endDateTime);
            utcDateEnd = dateend.toISOString();
          }

        formData.append("productName", productName);
        formData.append("productDescription", productDescription);
        formData.append("terms", terms);
        formData.append("categoryId", categoryId);
        formData.append("productPrice", productPrice);
        formData.append("totalWish", totalWish);
        formData.append("totalQuestions", totalQuestions);
        formData.append("status", status);
        formData.append("file", image);
        formData.append("productType", productType);
        formData.append("deductingWish", deductingWish);
        formData.append("isFirstWinnerGotProduct", isFirstWinnerGotProduct);
        formData.append("isPlayable", isPlayable);
        formData.append("isTimeBasedEvent", JSON.stringify(isTimeBasedEvent === "0" ? false : true));
        formData.append("startDateTime", utcDateStart);
        formData.append("endDateTime", utcDateEnd);

        return {
          url: "admin/product/create",
          method: "POST",
          body:  formData,
         
        };
      },
      invalidatesTags: ["Product"],
    }),
    updateProduct: build.mutation({
      query: ({ productName,
        _id,
        productDescription,
        terms,
        categoryId,
        productPrice,
        totalWish,
        totalQuestions,
        status,
        image,
        productType,
        deductingWish,
        isFirstWinnerGotProduct,isPlayable,
        isTimeBasedEvent,startDateTime,endDateTime,isHomeFeatured  }) => {
          let utcDateStart;
          let utcDateEnd;
          
          if(isTimeBasedEvent === "0"){
             const date = new Date();
             utcDateStart = date.toISOString();
            const dateend = new Date();
            utcDateEnd = dateend.toISOString();
          }else{
            const date = new Date(startDateTime);
            utcDateStart = date.toISOString();
            const dateend = new Date(endDateTime);
            utcDateEnd = dateend.toISOString();
          }
         

        return {
          url: `admin/product/update/${_id}`,
          method: "POST",
          body: {
            "productName" : productName,
            "productDescription" : productDescription,
            "terms":terms,
            "productPrice":productPrice,
            "totalWish":totalWish,
            "totalQuestions":totalQuestions,
            "status":status,
            "_id":_id,
            "productType" : productType,
            "deductingWish" : deductingWish,
            "isFirstWinnerGotProduct" : isFirstWinnerGotProduct,
            "isPlayable":isPlayable,
            "isTimeBasedEvent": isTimeBasedEvent === "0" ? false : true,
            "startDateTime": utcDateStart ?? "",
            "endDateTime" : utcDateEnd ?? "",
            "isHomeFeatured": Boolean(isHomeFeatured),
        },
        };
      },
      invalidatesTags: ["Product"],
    }),
    getUser: build.query({
      query: (id) => `general/user/${id}`,
      providesTags: ["User"],
    }),

    getTransactions: build.query({
      query: ({ page, pageSize, sort, search }) => ({
        url: "client/transactions",
        method: "GET",
        params: { page, pageSize, sort, search },
      }),
      providesTags: ["Transactions"],
    }),
    getGeography: build.query({
      query: () => "client/geography",
      providesTags: ["Geography"],
    }),
    getSales: build.query({
      query: () => "sales/sales",
      providesTags: ["Sales"],
    }),
    getAdmins: build.query({
      query: () => "management/admins",
      providesTags: ["Admins"],
    }),
    getUserPerformance: build.query({
      query: (id) => `management/performance/${id}`,
      providesTags: ["Performance"],
    }),
    getDashboard: build.query({
      query: () => "general/dashboard",
      providesTags: ["Dashboard"],
    }),
    createProductWinning: build.mutation({
      query: ({ winrange, productId, winpercent, amount, prizeType }) => {
        return {
          url: "admin/product/createProductWinning",
          method: "POST",
          body: {
            winrange,
            productId,
            winpercent,
            amount,
            prizeType: prizeType || "CASH",
          },
        };
      },
      invalidatesTags: ["Question"],
    }),
    getProductWinning: build.mutation({
      query: (productId) => ({
        url: `admin/product/getProductWinning/${productId}`,
        method: "GET",
      }),
      invalidatesTags: ["Category"],
    }),
    deleteProductWinning: build.mutation({
      query: (id) => ({
        url: `admin/product/deleteProductWinning/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Category"],
    }),
    updateProductWinning: build.mutation({
      query: ({ id, ...body }) => ({
        url: `admin/product/updateProductWinning/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Category"],
    }),
    createProductComboWinning: build.mutation({
      query: ({ comboRanks, productId, amount, prizeType, label }) => ({
        url: "admin/product/createProductComboWinning",
        method: "POST",
        body: {
          comboRanks,
          productId,
          amount,
          prizeType: prizeType || "CASH",
          label: label || undefined,
        },
      }),
      invalidatesTags: ["Category"],
    }),
    getProductComboWinning: build.mutation({
      query: (productId) => ({
        url: `admin/product/getProductComboWinning/${productId}`,
        method: "GET",
      }),
      invalidatesTags: ["Category"],
    }),
    deleteProductComboWinning: build.mutation({
      query: (id) => ({
        url: `admin/product/deleteProductComboWinning/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Category"],
    }),
    updateProductComboWinning: build.mutation({
      query: ({ id, ...body }) => ({
        url: `admin/product/updateProductComboWinning/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Category"],
    }),
    sendNotification: build.mutation({
      query: ({ title, body, imageUrl, data, userIds }) => {
        const validImageUrl = getValidNotificationImageUrl(imageUrl);
        return {
          url: "admin/notification/send",
          method: "POST",
          body: {
            title,
            body,
            ...(validImageUrl && { imageUrl: validImageUrl }),
            ...(data && { data }),
            ...(userIds && userIds.length > 0 && { userIds }),
          },
        };
      },
    }),
    getStartQuizV2Setting: build.query({
      query: () => ({
        url: "admin/settings/startQuizV2",
        method: "GET",
      }),
      providesTags: ["AppSettings"],
    }),
    updateStartQuizV2Setting: build.mutation({
      query: ({ startQuizV2Enabled }) => ({
        url: "admin/settings/startQuizV2",
        method: "PATCH",
        body: { startQuizV2Enabled },
      }),
      invalidatesTags: ["AppSettings"],
    }),
    getMinAppVersionSetting: build.query({
      query: () => ({
        url: "admin/settings/minAppVersion",
        method: "GET",
      }),
      providesTags: ["AppSettings"],
    }),
    updateMinAppVersionSetting: build.mutation({
      query: ({ minAppVersion }) => ({
        url: "admin/settings/minAppVersion",
        method: "PATCH",
        body: { minAppVersion },
      }),
      invalidatesTags: ["AppSettings"],
    }),
    getWithdrawalsSetting: build.query({
      query: () => ({
        url: "admin/settings/withdrawals",
        method: "GET",
      }),
      providesTags: ["WithdrawalsSettings"],
    }),
    updateWithdrawalsSetting: build.mutation({
      query: (body) => ({
        url: "admin/settings/withdrawals",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["WithdrawalsSettings"],
    }),
    getNameChangeSetting: build.query({
      query: () => ({
        url: "admin/settings/nameChange",
        method: "GET",
      }),
      providesTags: ["NameChangeSettings"],
    }),
    updateNameChangeSetting: build.mutation({
      query: (body) => ({
        url: "admin/settings/nameChange",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["NameChangeSettings"],
    }),
    getAdsSettings: build.query({
      query: () => ({
        url: "admin/settings/ads",
        method: "GET",
      }),
      providesTags: ["AdsSettings"],
    }),
    updateAdsSettings: build.mutation({
      query: (body) => ({
        url: "admin/settings/ads",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["AdsSettings"],
    }),
    updateAdScreen: build.mutation({
      query: ({ screenKey, enabled }) => ({
        url: `admin/settings/ads/${encodeURIComponent(screenKey)}`,
        method: "PATCH",
        body: { enabled },
      }),
      invalidatesTags: ["AdsSettings"],
    }),
    getLeaderboardStreakSettings: build.query({
      query: () => ({
        url: "admin/settings/leaderboardStreak",
        method: "GET",
      }),
      providesTags: ["LeaderboardStreakSettings"],
    }),
    updateLeaderboardStreakSettings: build.mutation({
      query: (body) => ({
        url: "admin/settings/leaderboardStreak",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["LeaderboardStreakSettings"],
    }),
    getReferralMessage: build.query({
      query: () => ({
        url: "admin/settings/referralMessage",
        method: "GET",
      }),
      providesTags: ["ReferralMessage"],
    }),
    updateReferralMessage: build.mutation({
      query: (body) => ({
        url: "admin/settings/referralMessage",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["ReferralMessage"],
    }),
    getLiveDashboardTickerSetting: build.query({
      query: () => ({
        url: "admin/settings/liveDashboardTicker",
        method: "GET",
      }),
      providesTags: ["LiveDashboardTicker"],
    }),
    updateLiveDashboardTickerSetting: build.mutation({
      query: ({ liveDashboardTickerMessage }) => ({
        url: "admin/settings/liveDashboardTicker",
        method: "PATCH",
        body: { liveDashboardTickerMessage },
      }),
      invalidatesTags: ["LiveDashboardTicker"],
    }),
    getListedLeaderboards: build.query({
      query: () => ({
        url: "admin/listed-leaderboard",
        method: "GET",
      }),
      providesTags: ["ListedLeaderboards"],
    }),
    createListedLeaderboard: build.mutation({
      query: (body) => ({
        url: "admin/listed-leaderboard",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ListedLeaderboards"],
    }),
    updateListedLeaderboard: build.mutation({
      query: ({ id, ...body }) => ({
        url: `admin/listed-leaderboard/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["ListedLeaderboards"],
    }),
    setListedLeaderboardVisibility: build.mutation({
      query: ({ id, visibleOnApp }) => ({
        url: `admin/listed-leaderboard/${id}/visibility`,
        method: "PATCH",
        body: { visibleOnApp },
      }),
      invalidatesTags: ["ListedLeaderboards"],
    }),
    setListedLeaderboardStatus: build.mutation({
      query: ({ id, adminStatus }) => ({
        url: `admin/listed-leaderboard/${id}/status`,
        method: "PATCH",
        body: { adminStatus },
      }),
      invalidatesTags: ["ListedLeaderboards"],
    }),
    uploadListedLeaderboardImage: build.mutation({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `admin/listed-leaderboard/${id}/image`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["ListedLeaderboards"],
    }),
  }),
});

// export api endpoints
export const {
  useGetUserQuery,
  useGetProductsQuery,
  useGetCustomersQuery,
  useGetCustomerWalletQuery,
  useUpdateCustomerUpiMutation,
  useUpdateCustomerWalletMutation,
  useGetTransactionsQuery,
  useGetGeographyQuery,
  useGetSalesQuery,
  useGetAdminsQuery,
  useGetUserPerformanceQuery,
  useGetDashboardQuery,
  useGetCategoryQuery,
  useDeleteCategoryMutation,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useCreateProductMutation,
  useDeleteProductMutation,
  useCloneProductMutation,
  useUpdateProductMutation,
  useAdminLoginMutation,
  useSaveProductImageMutation,
  useGetProductImagesQuery,
  useDeleteImageMutation,
  useCreateQuestionsMutation,
  useGetQuestionsQuery,
  useCreateProductQuestionsMutation,
  useGetProductQuestionsMutation,
  useDeleteProductQuestionMutation,
  useCreateProductWinningMutation,
  useGetProductWinningMutation,
  useDeleteProductWinningMutation,
  useUpdateProductWinningMutation,
  useCreateProductComboWinningMutation,
  useGetProductComboWinningMutation,
  useDeleteProductComboWinningMutation,
  useUpdateProductComboWinningMutation,
  useSendNotificationMutation,
  useGetWithdrawRequestsQuery,
  useUpdateWithdrawRequestStatusMutation,
  useGetStartQuizV2SettingQuery,
  useUpdateStartQuizV2SettingMutation,
  useGetMinAppVersionSettingQuery,
  useUpdateMinAppVersionSettingMutation,
  useGetWithdrawalsSettingQuery,
  useUpdateWithdrawalsSettingMutation,
  useGetNameChangeSettingQuery,
  useUpdateNameChangeSettingMutation,
  useGetAdsSettingsQuery,
  useUpdateAdsSettingsMutation,
  useUpdateAdScreenMutation,
  useGetLeaderboardStreakSettingsQuery,
  useUpdateLeaderboardStreakSettingsMutation,
  useGetReferralMessageQuery,
  useUpdateReferralMessageMutation,
  useGetLiveDashboardTickerSettingQuery,
  useUpdateLiveDashboardTickerSettingMutation,
  useGetProductWishesQuery,
  useGetContestLiveStatsQuery,
  useGetLiveDashboardStatsQuery,
  useGetPlayTableHistoryQuery,
  useGetComboWinnersQuery,
  useCompleteProductWishMutation,
  useAnnounceProductWishResultMutation,
  useSetProductActiveStatusMutation,
  useSetProductPlayableStatusMutation,
  useGetListedLeaderboardsQuery,
  useCreateListedLeaderboardMutation,
  useUpdateListedLeaderboardMutation,
  useSetListedLeaderboardVisibilityMutation,
  useSetListedLeaderboardStatusMutation,
  useUploadListedLeaderboardImageMutation,
} = api;
